import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtDateTime, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, FileText, ExternalLink, Trash2, FolderLock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import EmptyState from "@/components/EmptyState";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Documents() {
    const { hasRole } = useAuth();
    const [sp, setSp] = useSearchParams();
    const [cards, setCards] = useState([]);
    const [types, setTypes] = useState([]);
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [cardFilter, setCardFilter] = useState("all");
    const [typeFilter, setTypeFilter] = useState(sp.get("type") || "all");
    const [f, setF] = useState({ card_id: "", doc_type: "", title: "", remarks: "" });
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);

    // Keep URL and typeFilter in sync so the sidebar deep-link
    // (?type=warranty, ?type=receipt, ...) always lands the user on the
    // correct filtered view.
    useEffect(() => {
        const urlType = sp.get("type") || "all";
        if (urlType !== typeFilter) setTypeFilter(urlType);
    }, [sp, typeFilter]);

    const load = () => {
        const params = {};
        if (cardFilter !== "all") params.card_id = cardFilter;
        if (typeFilter !== "all") params.doc_type = typeFilter;
        api.get("/documents", { params }).then((r) => setRows(r.data));
    };
    useEffect(() => {
        api.get("/cards").then((r) => setCards(r.data));
        api.get("/documents/types").then((r) => {
            setTypes(r.data.types);
            setF((s) => ({ ...s, doc_type: r.data.types[0] || "" }));
        });
    }, []);
    useEffect(() => { load(); }, [cardFilter, typeFilter]);

    const setInput = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
    const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!file) return toast.error("Choose a file first");
        setUploading(true);
        const fd = new FormData();
        if (f.card_id) fd.append("card_id", f.card_id);
        fd.append("doc_type", f.doc_type);
        fd.append("title", f.title);
        if (f.remarks) fd.append("remarks", f.remarks);
        fd.append("file", file);
        try {
            await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Document uploaded");
            setOpen(false);
            setFile(null);
            setF({ card_id: "", doc_type: types[0] || "", title: "", remarks: "" });
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUploading(false);
        }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this document?")) return;
        try {
            await api.delete(`/documents/${id}`);
            toast.success("Deleted");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const cardMap = Object.fromEntries(cards.map((c) => [c.id, c]));

    return (
        <div data-testid="documents-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow">Document Vault</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">Every letter, every proof.</h1>
                    <p className="text-sm text-stone-500 mt-3">{rows.length} documents on file.</p>
                </div>
                {hasRole("admin", "finance") && (
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="upload-doc-btn"><Upload size={16} className="mr-1.5" /> Upload document</Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader><DialogTitle className="font-display text-2xl">Upload a document</DialogTitle></DialogHeader>
                            <form onSubmit={submit} className="space-y-3">
                                <div>
                                    <Label className="eyebrow">Document type</Label>
                                    <Select value={f.doc_type} onValueChange={set("doc_type")}>
                                        <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                        <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="eyebrow">Title</Label>
                                    <Input required value={f.title} onChange={setInput("title")} className="mt-1.5" />
                                </div>
                                <div>
                                    <Label className="eyebrow">Card (optional)</Label>
                                    <Select value={f.card_id || "none"} onValueChange={(v) => set("card_id")(v === "none" ? "" : v)}>
                                        <SelectTrigger className="mt-1.5"><SelectValue placeholder="No card" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">— No card —</SelectItem>
                                            {cards.map((c) => <SelectItem key={c.id} value={c.id}>{c.bank_name} · •••• {c.last_4}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="eyebrow">File</Label>
                                    <Input type="file" required onChange={(e) => setFile(e.target.files[0])} className="mt-1.5" />
                                </div>
                                <div>
                                    <Label className="eyebrow">Remarks</Label>
                                    <Textarea rows={2} value={f.remarks} onChange={setInput("remarks")} className="mt-1.5" />
                                </div>
                                <Button type="submit" disabled={uploading} className="w-full bg-stone-900 hover:bg-stone-800 text-white">
                                    {uploading ? "Uploading…" : "Upload"}
                                </Button>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            <div className="flex flex-wrap gap-3 mb-4">
                <Select value={cardFilter} onValueChange={setCardFilter}>
                    <SelectTrigger className="w-[240px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All cards</SelectItem>
                        {cards.map((c) => <SelectItem key={c.id} value={c.id}>{c.bank_name} · •••• {c.last_4}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger className="w-[240px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All types</SelectItem>
                        {types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>

            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="dense-table w-full">
                        <thead>
                            <tr><th>Uploaded</th><th>Title</th><th>Type</th><th>Card</th><th>By</th><th>File</th><th></th></tr>
                        </thead>
                        <tbody>
                            {rows.map((d) => {
                                const c = cardMap[d.card_id];
                                return (
                                    <tr key={d.id}>
                                        <td className="text-xs tabular-nums">{fmtDateTime(d.uploaded_at)}</td>
                                        <td className="font-medium">{d.title}</td>
                                        <td className="text-xs uppercase tracking-widest text-stone-500">{d.doc_type}</td>
                                        <td className="text-xs">{c ? `${c.bank_name} · •••• ${c.last_4}` : "—"}</td>
                                        <td className="text-xs">{d.uploaded_by_name}</td>
                                        <td>
                                            {d.file_url && (
                                                <a href={`${BACKEND_URL}${d.file_url}`} target="_blank" rel="noreferrer" className="text-stone-700 hover:text-stone-900 inline-flex items-center gap-1 text-xs">
                                                    <FileText size={12} /> View <ExternalLink size={10} />
                                                </a>
                                            )}
                                        </td>
                                        <td>
                                            {hasRole("admin") && (
                                                <button onClick={() => del(d.id)} className="text-stone-400 hover:text-red-600"><Trash2 size={14} /></button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            {rows.length === 0 && (
                                <tr><td colSpan={7} className="p-0"><EmptyState
                                    icon={FolderLock}
                                    title="Keep important financial records organised."
                                    hint="Upload receipts, warranties, insurance PDFs or asset proofs — filter by type from the sidebar."
                                    cta={hasRole("admin", "finance") ? { to: "#", label: "Upload document", testId: "empty-documents-cta" } : null}
                                    demoTo="/documents"
                                    className="rounded-none border-0 py-12"
                                /></td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
